"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  IconButton,
  Snackbar,
  SnackbarContent,
  type SnackbarProps,
} from "@mui/material";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ShowNotificationOptions {
  key?: string;
  severity?: "info" | "warning" | "error" | "success";
  autoHideDuration?: number;
  actionText?: ReactNode;
  onAction?: () => void;
}

export interface NotificationsProviderSlotProps {
  snackbar?: Partial<SnackbarProps>;
}

interface NotificationsApi {
  show: (message: ReactNode, options?: ShowNotificationOptions) => string;
  close: (key: string) => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const NotificationsContext = createContext<NotificationsApi | null>(null);

// ─── Internal entry ──────────────────────────────────────────────────────────

interface NotificationEntry {
  key: string;
  message: ReactNode;
  options: ShowNotificationOptions;
  open: boolean;
}

let keyCounter = 0;

// ─── Provider ────────────────────────────────────────────────────────────────

export function NotificationsProvider({
  children,
  slotProps,
}: {
  children: ReactNode;
  slotProps?: NotificationsProviderSlotProps;
}) {
  const [queue, setQueue] = useState<NotificationEntry[]>([]);

  const show = useCallback(
    (message: ReactNode, options: ShowNotificationOptions = {}): string => {
      const key = options.key ?? `notification-${String(keyCounter++)}`;
      setQueue((prev) => {
        if (prev.some((n) => n.key === key)) return prev;
        return [...prev, { key, message, options, open: true }];
      });
      return key;
    },
    [],
  );

  const close = useCallback((key: string) => {
    setQueue((prev) =>
      prev.map((n) => (n.key === key ? { ...n, open: false } : n)),
    );
    setTimeout(() => {
      setQueue((prev) => prev.filter((n) => n.key !== key));
    }, 500);
  }, []);

  const api = useMemo(() => ({ show, close }), [show, close]);

  // Show only the first notification; badge if more are queued.
  const currentKey = queue.find((n) => n.open)?.key ?? null;

  return (
    <NotificationsContext.Provider value={api}>
      {children}
      {queue.map(({ key, message, options, open: isOpen }) => {
        const {
          severity,
          autoHideDuration = 6000,
          actionText,
          onAction,
        } = options;
        const isVisible = key === currentKey;

        const action = (
          <>
            {onAction && actionText && (
              <Button color="inherit" size="small" onClick={onAction}>
                {actionText}
              </Button>
            )}
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => {
                close(key);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        );

        return (
          <Snackbar
            key={key}
            open={isOpen && isVisible}
            autoHideDuration={autoHideDuration}
            onClose={(_, reason) => {
              if (reason !== "clickaway") close(key);
            }}
            action={severity ? undefined : action}
            {...slotProps?.snackbar}
          >
            {severity ? (
              <Alert severity={severity} sx={{ width: "100%" }} action={action}>
                {message}
              </Alert>
            ) : (
              <SnackbarContent message={message} action={action} />
            )}
          </Snackbar>
        );
      })}
    </NotificationsContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useNotifications(): NotificationsApi {
  const ctx = useContext(NotificationsContext);
  if (!ctx)
    throw new Error(
      "useNotifications must be used within a NotificationsProvider",
    );
  return ctx;
}
